import React, { useState, useEffect, useCallback, useRef } from 'react';
import CategorieService from '../../services/CategorieService';

import {
  Box, Typography, TextField, InputAdornment, Button, IconButton,
  useMediaQuery, ThemeProvider, createTheme,
  Snackbar, Alert, CircularProgress,
} from '@mui/material';
import { Directions as DirectionsIcon } from '@mui/icons-material';
import {
  Search as SearchIcon,
  LocationOn as MapPinIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  Schedule as ClockIcon,
  Close as CloseIcon,
  MyLocation as NavigationIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { GoogleMap, MarkerF, useJsApiLoader, InfoWindowF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import listeAffichageDynamique from './ListeAffichageDynamique';
import { REACT_APP_LIFERAY_HOST } from '../../services/utils';

/* ─────────────────────────────────────────────────────────────
   THÈME MUI
   ───────────────────────────────────────────────────────────── */
const theme = createTheme({
  palette: { primary: { main: '#2e7d32' }, secondary: { main: '#4caf50' } },
});

/* ─────────────────────────────────────────────────────────────
   HELPER : normalise une chaîne pour la recherche
   ───────────────────────────────────────────────────────────── */
const normalize = (str = '') =>
  str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

/* ─────────────────────────────────────────────────────────────
   CONSTANTES
   ───────────────────────────────────────────────────────────── */
const center       = { lat: 7.5, lng: -5.5 };
const CIV_BOUNDS   = { north: 10.74, south: 4.357, west: -8.601, east: -2.494 };
const PADDING_FULL = { top: 8,  right: 8,  bottom: 8,   left: 8   };
const PADDING_CARD = { top: 40, right: 40, bottom: 110, left: 40  };
const PANEL_WIDTH  = 400;

// Liferay DXP dockbar/control-panel tourne autour de z-index 1000-9000.
// On décale nos valeurs critiques pour rester au-dessus à l'intérieur
// du portlet, sans interférer avec la navigation globale de la page.
const Z = {
  mapButtons:  10,
  loading:     20,
  overlay:     30,   // overlay 2-doigts — dans le portlet, pas besoin de monter plus
  bottomSheet: 10,
  bandeau:     20,
  agenceCard:  50,   // réduit : isolation: 'isolate' gère le stacking context
  desktopPanel: 40,
  desktopSearch: 50,
  desktopButtons: 45,
  desktopLoading: 60,
};

const BANDEAU_H       = 100;
const LISTE_H         = '48dvh';
// Liferay DXP injecte une top-bar (~48px en edit mode, 0 en view mode).
// On utilise --bicici-map-vh injecté par le portlet si dispo, sinon 100dvh.
const FULL_H          = 'var(--bicici-map-vh, 100dvh)';
const CARTE_REDUITE_H = `calc(${FULL_H} - ${BANDEAU_H}px - 48dvh)`;

/* ─────────────────────────────────────────────────────────────
   HELPERS COORDONNÉES
   ───────────────────────────────────────────────────────────── */
const isNum   = (v) => typeof v === 'number' && Number.isFinite(v);
const inWorld = (lat, lng) =>
  isNum(lat) && isNum(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
const inCI    = (lat, lng) =>
  lat >= 4 && lat <= 10.9 && lng >= -8.9 && lng <= -2.3;

/* ─────────────────────────────────────────────────────────────
   sanitizeAgence
   ───────────────────────────────────────────────────────────── */
const sanitizeAgence = (d) => {
  const baseUrl = window?.Liferay
    ? window.Liferay.ThemeDisplay.getPortalURL()
    : 'https://bicici.ci';

  let lat = Number(d.latitude);
  let lng = Number(d.longitude);

  if (isNum(lng) && lng > 0 && lng <= 9) lng = -lng;
  if (!inCI(lat, lng) && inCI(lng, lat)) [lat, lng] = [lng, lat];
  if (!inWorld(lat, lng)) return null;

  lat = Number(lat.toFixed(6));
  lng = Number(lng.toFixed(6));

  const thumbnailURL = d.image?.thumbnailURL || '';
  const hrefURL = (d.image?.link?.href || '')
    .replace(/[&?]download=true/g, '')
    .replace(/&objectDefinitionExternalReferenceCode=[^&]*/g, '')
    .replace(/&objectEntryExternalReferenceCode=[^&]*/g, '');
  const rawImageUrl = thumbnailURL || hrefURL;

  return {
    id:            d.id,
    nom:           d.nom       || '',
    adresse:       d.adresse   || '',
    ville:         d.ville     || '',
    telephone:     d.telephone || '',
    email:         '',
    heures:        d.heure     || '',
    imageUrl:      rawImageUrl ? `${baseUrl}${rawImageUrl}` : '',
    position:      { lat, lng },
    ouvert:        true,
    dab:           true,
    serviceClient: true,
  };
};

/* ═══════════════════════════════════════════════════════════════
   LOGO BICICI
   ═══════════════════════════════════════════════════════════════ */
const BiciciLogoBox = () => (
  <Box
    sx={{
      width:          46,
      height:         46,
      flexShrink:     0,
      borderRadius:   '14px',
      overflow:       'hidden',
      bgcolor:        '#0f5132',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
    }}
  >
    <Box
      component="img"
      src="/images/logobicici.png"
      alt="BICICI"
      loading="lazy"
      onError={(e) => {
        if (e.currentTarget.dataset.fallback) return;
        e.currentTarget.dataset.fallback = '1';
        e.currentTarget.src = `${REACT_APP_LIFERAY_HOST}/documents/d/website-bicici/logobicici`;
      }}
      sx={{
        width:     '58%',
        height:    '58%',
        objectFit: 'contain',
        display:   'block',
      }}
    />
  </Box>
);

/* ═══════════════════════════════════════════════════════════════
   COMPOSANT AgenceMobileCard
   ═══════════════════════════════════════════════════════════════ */
const AgenceMobileCard = ({ agence, selected, onClick, onCall, onDirections, onShare }) => (
  <Box
    onClick={onClick}
    sx={{
      display:      'flex',
      alignItems:   'center',
      gap:          1.25,
      mb:           1.25,
      bgcolor:      '#ffffff',
      borderRadius: '16px',
      border:       '1.5px solid',
      borderColor:  selected ? '#2e7d32' : 'rgba(0,0,0,0.09)',
      boxShadow:    selected
        ? '0 3px 14px rgba(46,125,50,0.18)'
        : '0 1px 6px rgba(0,0,0,0.07)',
      p:            1.25,
      cursor:       'pointer',
      transition:   'border-color 0.2s, box-shadow 0.2s',
      '&:active':   { opacity: 0.92 },
      overflow:     'hidden',
    }}
  >
    <BiciciLogoBox />

    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Ligne 1 : nom + badge */}
      <Box sx={{
        display:        'flex',
        alignItems:     'flex-start',
        justifyContent: 'space-between',
        gap:            0.75,
        mb:             0.85,
      }}>
        <Typography sx={{
          fontWeight: 700,
          fontSize:   '0.9rem',
          color:      '#111',
          lineHeight: 1.3,
          flex:       1,
          minWidth:   0,
        }}>
          {agence.nom}
        </Typography>

        {agence.ouvert ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2e7d32', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#2e7d32', lineHeight: 1, whiteSpace: 'nowrap' }}>Ouvert</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#c62828', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#c62828', lineHeight: 1, whiteSpace: 'nowrap' }}>Fermé</Typography>
          </Box>
        )}
      </Box>

      {/* Ligne 2 : 3 boutons pill */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {[
          {
            label: 'Appeler',
            fn: () => onCall && onCall(agence),
            icon: <svg width="12" height="12" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="#2e7d32"/></svg>,
          },
          {
            label: 'Itinéraire',
            fn: () => onDirections && onDirections(agence),
            icon: <svg width="12" height="12" viewBox="0 0 24 24"><path d="M21.71 11.29l-9-9a1 1 0 00-1.42 0l-9 9a1 1 0 000 1.42l9 9a1 1 0 001.42 0l9-9a1 1 0 000-1.42zM14 14.5V12h-4v3H8v-4a1 1 0 011-1h5V7.5l3.5 3.5-3.5 3.5z" fill="#2e7d32"/></svg>,
          },
          {
            label: 'Partager',
            fn: () => onShare && onShare(agence),
            icon: <svg width="12" height="12" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" fill="#2e7d32"/></svg>,
          },
        ].map(({ label, fn, icon }) => (
          <Box
            key={label}
            component="button"
            onClick={(e) => { e.stopPropagation(); fn(); }}
            sx={{
              display:      'inline-flex',
              alignItems:   'center',
              gap:          '4px',
              bgcolor:      '#fff',
              border:       '1.5px solid #2e7d32',
              borderRadius: 9999,
              px:           '10px',
              py:           '5px',
              cursor:       'pointer',
              outline:      'none',
              fontFamily:   'inherit',
              transition:   'background-color 0.15s',
              '&:active':   { bgcolor: '#e8f5e9' },
            }}
          >
            {icon}
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2e7d32', lineHeight: 1, whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);


/* ═══════════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL BiciciMap
   ═══════════════════════════════════════════════════════════════ */
const BiciciMap = () => {

  const [afficherListeBg, setAfficherListeBg] = useState('#82E19A');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setAfficherListeBg(listeAffichageDynamique(window.location.pathname));
  }, []);

  /* ─────────────────────────────────
     ÉTATS
     ───────────────────────────────── */
  const [agences,          setAgences]          = useState([]);
  const [filteredAgences,  setFilteredAgences]  = useState([]);
  const [selectedAgence,   setSelectedAgence]   = useState(null);

  const isMobile         = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [showList,      setShowList]      = useState(false);
  const [userPosition,  setUserPosition]  = useState(null);
  const [customPin,     setCustomPin]     = useState(null);
  const [map,           setMap]           = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [hoveredAgence, setHoveredAgence] = useState(null);

  const fullScreenRef  = useRef(false);
  const setFullScreenSafe = useCallback((val) => {
    fullScreenRef.current = val;
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const searchRef      = useRef(null);
  const mapRef         = useRef(null);
  const initialViewRef = useRef(null);
  const hoverTimerRef  = useRef(null);
  const [notFoundMsg,  setNotFoundMsg]  = useState('');

  /* ─────────────────────────────────────────────────────────────
     OVERLAY CARTE — logique 2 doigts
     ─────────────────────────────────────────────────────────────
     FIX 1 — BLOCAGE INTERACTIONS :
       L'overlay a désormais pointerEvents: 'auto' sur le container
       principal pour bloquer TOUTE interaction avec la carte dessous.
       Seul le bouton CTA garde pointerEvents: 'auto' (inchangé).

     FIX TRANSITION BRUSQUE : hintVisible pilote l'opacité CSS en
       sortie (fade-out avant de retirer l'élément du DOM).
     ───────────────────────────────────────────────────────────── */
  const [showMapHint,    setShowMapHint]    = useState(true);
  const [hintVisible,    setHintVisible]    = useState(true);
  const hintTimerRef     = useRef(null);
  const mapContainerRef  = useRef(null);

  /* dismissHint : fade-out d'abord (600 ms), puis retire du DOM */
  const dismissHint = useCallback(() => {
    clearTimeout(hintTimerRef.current);
    setHintVisible(false);
    hintTimerRef.current = setTimeout(() => {
      setShowMapHint(false);
    }, 600);
  }, []);

  /* Écoute les touches sur le container carte — 2 doigts → ferme l'overlay */
  useEffect(() => {
    if (!isMobileOrTablet) {
      setShowMapHint(false);
      setHintVisible(false);
      return;
    }

    if (!showMapHint) return;

    const onTouchStart = (e) => {
      if (e.touches.length >= 2) {
        e.stopPropagation();
        dismissHint();
      }
    };

    const container = mapContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', onTouchStart, { passive: true });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
    };
  }, [isMobileOrTablet, showMapHint, dismissHint]);

  /* ─────────────────────────────────
     GOOGLE MAPS
     ───────────────────────────────── */
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const onLoad = useCallback((m) => {
    setMap(m);
    if (window.google?.maps) {
      m.setOptions({
        mapTypeControlOptions: {
          style:    window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
        },
      });
    }
  }, []);

  const onUnmount = useCallback(() => setMap(null), []);

  /* ─────────────────────────────────
     FETCH agences
     ───────────────────────────────── */
  useEffect(() => {
    const svc = new CategorieService();
    setLoading(true);
    svc.allCategorie()
      .then((res) => {
        const list = Array.isArray(res?.items) ? res.items
          : Array.isArray(res) ? res
          : Object.values(res || {});
        const mapped = list.map(sanitizeAgence).filter(Boolean);
        setAgences(mapped);
        setFilteredAgences(mapped);
        setFullScreenSafe(false);
      })
      .catch((e) => console.error('Erreur API agences:', e))
      .finally(() => setLoading(false));
  }, []);

  /* ─────────────────────────────────
     FILTRE recherche
     ───────────────────────────────── */
  useEffect(() => {
    const q = normalize(searchQuery);
    if (q.length === 0) { setFilteredAgences(agences); return; }
    const results = agences
      .map(a => {
        let score = 0;
        if (normalize(a.nom).includes(q))     score += 3;
        if (normalize(a.adresse).includes(q)) score += 2;
        if (normalize(a.ville).includes(q))   score += 1;
        return { ...a, _score: score };
      })
      .filter(a => a._score > 0)
      .sort((a, b) => b._score - a._score);
    setFilteredAgences(results);
  }, [searchQuery, agences]);

  useEffect(() => {
    if (searchQuery.length > 0) setFullScreenSafe(false);
  }, [searchQuery]);

  /* ─────────────────────────────────
     FIX BUG "Votre demande n'aboutit pas"
     ───────────────────────────────── */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length === 0 || loading) { setNotFoundMsg(''); return; }
    if (filteredAgences.length === 0) setNotFoundMsg(`Aucune agence pour "${q}" dans ce secteur.`);
    else setNotFoundMsg('');
  }, [filteredAgences, searchQuery, loading]);

  /* ─────────────────────────────────
     GÉOLOCALISATION
     ───────────────────────────────── */
  const handleLocateUser = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserPosition(p);
        setFullScreenSafe(false);
        if (map) map.panTo(p);
      },
      (err) => console.error('Erreur de géolocalisation:', err)
    );
  };

  /* ─────────────────────────────────
     ITINÉRAIRE
     ───────────────────────────────── */
  const openDirections = (dest) => {
    if (!dest || !isNum(dest.lat) || !isNum(dest.lng)) return;
    const hasOrigin = userPosition && isNum(userPosition.lat) && isNum(userPosition.lng);
    const url = hasOrigin
      ? `https://www.google.com/maps/dir/?api=1&origin=${userPosition.lat},${userPosition.lng}&destination=${dest.lat},${dest.lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}`;
    window.open(encodeURI(url), '_blank');
  };

  /* ─────────────────────────────────
     PARTAGER
     ───────────────────────────────── */
  const handleShare = (agence) => {
    const text = `${agence.nom} — ${agence.adresse}`;
    const url = `https://www.google.com/maps/search/?api=1&query=${agence.position.lat},${agence.position.lng}`;
    if (navigator.share) {
      navigator.share({ title: agence.nom, text, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(`${text}\n${url}`).catch(() => {});
    }
  };

  /* ─────────────────────────────────
     APPELER
     ───────────────────────────────── */
  const handleCall = (agence) => {
    if (!agence.telephone) return;
    // Utilise un <a> temporaire pour éviter que Liferay intercepte
    // window.location.href et déclenche une navigation de portlet.
    const a = document.createElement('a');
    a.href = `tel:${agence.telephone}`;
    a.click();
  };

  /* ─────────────────────────────────
     GESTURE HANDLING
     ───────────────────────────────── */
  useEffect(() => {
    if (!map || !isMobileOrTablet) return;
    map.setOptions({ gestureHandling: showMapHint ? 'cooperative' : 'greedy' });
  }, [map, showMapHint, isMobileOrTablet]);

  /* ─────────────────────────────────
     fitBounds CI
     ───────────────────────────────── */
  useEffect(() => {
    if (!map || !isLoaded) return;
    const b = new window.google.maps.LatLngBounds(
      { lat: CIV_BOUNDS.south, lng: CIV_BOUNDS.west },
      { lat: CIV_BOUNDS.north, lng: CIV_BOUNDS.east }
    );
    const padding = isMobile
      ? { top: 20, right: 20, bottom: 20, left: 20 }
      : (fullScreenRef.current ? PADDING_FULL : PADDING_CARD);
    map.fitBounds(b, padding);

    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      const correctCenter = isMobile ? { lat: 7.2, lng: -5.5 } : { lat: 7, lng: -3.7 };
      map.setCenter(correctCenter);
      map.setZoom(isMobile ? 6 : 7);
      if (!initialViewRef.current) {
        initialViewRef.current = { zoom: isMobile ? 6 : 7, center: correctCenter };
      }
    });
  }, [map, isLoaded]);

  /* ─────────────────────────────────
     RESET
     ───────────────────────────────── */
  const resetMapView = useCallback(() => {
    setSelectedAgence(null);
    if (!isMobileOrTablet) setShowList(true);
    setSearchQuery('');
    setFilteredAgences(agences);
    if (map && initialViewRef.current) {
      map.setZoom(initialViewRef.current.zoom);
      map.panTo(initialViewRef.current.center);
    }
  }, [agences, map, isMobileOrTablet]);

  /* ─────────────────────────────────
     FALLBACK image
     ───────────────────────────────── */
  const handleImgError = (e) => {
    if (e.currentTarget.src.includes('map.jpg')) return;
    e.currentTarget.onerror = null;
    e.currentTarget.src = '/images/map.jpg';
  };

  /* ─────────────────────────────────
     MARKERS + CLUSTERING
     ───────────────────────────────── */
  useEffect(() => {
    if (!map || !isLoaded) return;
    const markers = [];

    filteredAgences.forEach((agence) => {
      try {
        const m = new window.google.maps.Marker({
          position: agence.position,
          title:    agence.nom,
          cursor:   'pointer',
          icon: {
            url:        `${REACT_APP_LIFERAY_HOST}/documents/d/website-bicici/logobicici`,
            scaledSize: new window.google.maps.Size(30, 40),
            anchor:     new window.google.maps.Point(15, 40),
          },
        });

        m.addListener('mouseover', () => { clearTimeout(hoverTimerRef.current); setHoveredAgence(agence); });
        m.addListener('mouseout',  () => { hoverTimerRef.current = setTimeout(() => setHoveredAgence(null), 300); });
        m.addListener('click', () => {
          setSelectedAgence(agence);
          setFullScreenSafe(false);
          setShowList(false);
          if (map) { map.panTo(agence.position); map.setZoom(isMobile ? 12 : 15); }
        });

        try { m.__agence = agence; } catch (e) { /* noop */ }
        markers.push(m);
      } catch (e) { console.warn('marker creation failed', e); }
    });

    const clusterer = new MarkerClusterer({ markers, map, maxZoom: 15 });

    try {
      clusterer.addListener && clusterer.addListener('click', (cluster) => {
        try {
          const cms = cluster.markers || (cluster.getMarkers && cluster.getMarkers()) || [];
          if (!cms.length) return;
          const bounds = new window.google.maps.LatLngBounds();
          cms.forEach((m) => bounds.extend(m.getPosition()));
          if (map) {
            map.panTo(bounds.getCenter());
            map.setZoom(Math.min((map.getZoom() || 8) + (isMobile ? 1 : 2), 16));
          }
          const agencesList = cms.map((m) => m.__agence).filter(Boolean);
          if (agencesList.length) { setFilteredAgences(agencesList); setShowList(true); setSelectedAgence(null); }
        } catch (e) { console.warn('cluster click handler error', e); }
      });
    } catch (e) { console.warn('cannot attach cluster click listener', e); }

    return () => {
      try { clusterer.clearMarkers(); } catch (e) {}
      markers.forEach((m) => {
        try {
          m.setMap(null);
          if (window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(m);
        } catch (e) {}
      });
    };
  }, [map, isLoaded, filteredAgences]);

  /* Desktop layout helpers */
  const SEARCH_LEFT  = showList && !isMobileOrTablet ? `calc(50% - ${PANEL_WIDTH / 2}px)` : '50%';
  const SEARCH_WIDTH = showList && !isMobileOrTablet ? `calc(100% - ${PANEL_WIDTH + 48}px)` : undefined;

  /* ═══════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════ */
  return (
    <div id="bicici-map-root" style={{ isolation: 'isolate' }}>
    <ThemeProvider theme={theme}>

      {/* ════════════════════════════════════════════════════════
          LAYOUT MOBILE / TABLETTE
          ════════════════════════════════════════════════════════ */}
      {isMobileOrTablet ? (
        <Box
          sx={{
            width:         '100%',
            height:        FULL_H,
            display:       'flex',
            flexDirection: 'column',
            overflow:      'hidden',
          }}
        >

          {/* ── 1) CARTE ── */}
          <Box
            ref={(el) => { mapRef.current = el; mapContainerRef.current = el; }}
            sx={{
              position:   'relative',
              width:      '100%',
              flexShrink: 0,
              flex:       showList ? 'none' : '1 1 auto',
              height:     showList ? CARTE_REDUITE_H : 'auto',
              minHeight:  showList ? '30dvh' : 'auto',
              transition: 'height 0.35s cubic-bezier(0.4,0,0.2,1), flex 0.35s cubic-bezier(0.4,0,0.2,1)',
              zIndex:     1,
            }}
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={(e) => {
                  if (showMapHint) return;
                  setCustomPin({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                  setFullScreenSafe(false);
                }}
                options={{
                  gestureHandling:   showMapHint ? 'cooperative' : 'greedy',
                  zoomControl:       false,
                  mapTypeControl:    false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  rotateControl:     false,
                  scaleControl:      false,
                }}
              >
                {userPosition && (
                  <MarkerF
                    position={userPosition}
                    icon={{
                      url: 'data:image/svg+xml;base64,' + btoa(
                        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2196f3" width="24" height="24"><circle cx="12" cy="12" r="6"/></svg>`
                      ),
                      scaledSize: new window.google.maps.Size(20, 20),
                      anchor:     new window.google.maps.Point(10, 10),
                    }}
                  />
                )}
                {customPin && (
                  <MarkerF
                    position={customPin}
                    draggable
                    onDragEnd={(e) => setCustomPin({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                    icon={{
                      url: 'data:image/svg+xml;base64,' + btoa(
                        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6d00" width="28" height="28"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>`
                      ),
                      scaledSize: new window.google.maps.Size(28, 28),
                      anchor:     new window.google.maps.Point(14, 28),
                    }}
                  />
                )}
              </GoogleMap>
            ) : (
              <Box sx={{ width: '100%', height: '100%', bgcolor: '#e8e8e8' }} />
            )}

            {/* Bouton reset */}
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resetMapView();
              }}
              title="Retour à la carte"
              sx={{
                position:   'absolute',
                top:        `calc(12px + env(safe-area-inset-top, 0px))`,
                right:      12,
                bgcolor:    'rgba(255,255,255,0.95)',
                border:     '1px solid rgba(0,0,0,0.12)',
                '&:hover':  { bgcolor: '#f5f5f5' },
                zIndex:     Z.mapButtons, width: 36, height: 36,
              }}
            >
              <ResetIcon color="primary" sx={{ fontSize: 18 }} />
            </IconButton>

            {/* Bouton géolocalisation */}
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleLocateUser();
              }}
              sx={{
                position:   'absolute',
                top:        `calc(12px + env(safe-area-inset-top, 0px))`,
                left:       12,
                bgcolor:    'rgba(255,255,255,0.95)',
                border:     '1px solid rgba(0,0,0,0.12)',
                '&:hover':  { bgcolor: '#f5f5f5' },
                zIndex:     Z.mapButtons, width: 36, height: 36,
              }}
            >
              <NavigationIcon color="primary" sx={{ fontSize: 18 }} />
            </IconButton>

            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: Z.loading }}>
                <CircularProgress color="primary" size={36} />
                <Typography variant="caption" color="primary" fontWeight={600} sx={{ mt: 1 }}>Chargement…</Typography>
              </Box>
            )}

            {/* ══════════════════════════════════════════════════════
                OVERLAY 2 DOIGTS
                FIX 1 — pointerEvents: 'auto' sur le container pour
                bloquer toute interaction avec la carte en dessous.
                Seul le bouton CTA conserve pointerEvents: 'auto'.
            ══════════════════════════════════════════════════════ */}
            {showMapHint && !loading && (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                onTouchMove={(e) => {
                  e.stopPropagation();
                }}
                sx={{
                  position:             'absolute',
                  inset:                0,
                  zIndex:               Z.overlay,
                  display:              'flex',
                  flexDirection:        'column',
                  alignItems:           'center',
                  justifyContent:       'center',
                  background:           'linear-gradient(160deg, rgba(0,30,15,0.72) 0%, rgba(0,60,25,0.80) 100%)',
                  backdropFilter:       'blur(3px)',
                  WebkitBackdropFilter: 'blur(3px)',
                  /* ── FIX 1 : était 'none', maintenant 'auto' pour
                     bloquer tous les events vers la carte          ── */
                  pointerEvents:        'auto',
                  userSelect:           'none',
                  /* ── Transition douce ── */
                  opacity:              hintVisible ? 1 : 0,
                  transition:           'opacity 0.55s cubic-bezier(0.4,0,0.2,1)',
                }}
              >
                {/* Carte visuelle : deux doigts animés */}
                <Box sx={{ position: 'relative', width: 100, height: 100, mb: 3 }}>
                  <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    border: '2px solid rgba(76,175,80,0.4)',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%,100%': { transform: 'scale(1)',    opacity: 0.6  },
                      '50%':     { transform: 'scale(1.22)', opacity: 0.15 },
                    },
                  }} />
                  <Box sx={{
                    position: 'absolute', top: '30%', left: '18%',
                    animation: 'fingerLeft 2s ease-in-out infinite',
                    '@keyframes fingerLeft': {
                      '0%,100%': { transform: 'translate(0,0)' },
                      '50%':     { transform: 'translate(-8px,-6px)' },
                    },
                  }}>
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                      <rect x="6" y="0" width="20" height="28" rx="10" fill="white" fillOpacity="0.95"/>
                      <rect x="6" y="20" width="20" height="16" rx="4" fill="white" fillOpacity="0.95"/>
                      <circle cx="16" cy="10" r="5" fill="#4caf50"/>
                    </svg>
                  </Box>
                  <Box sx={{
                    position: 'absolute', top: '30%', right: '18%',
                    animation: 'fingerRight 2s ease-in-out infinite',
                    '@keyframes fingerRight': {
                      '0%,100%': { transform: 'translate(0,0)' },
                      '50%':     { transform: 'translate(8px,-6px)' },
                    },
                  }}>
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                      <rect x="6" y="0" width="20" height="28" rx="10" fill="white" fillOpacity="0.95"/>
                      <rect x="6" y="20" width="20" height="16" rx="4" fill="white" fillOpacity="0.95"/>
                      <circle cx="16" cy="10" r="5" fill="#4caf50"/>
                    </svg>
                  </Box>
                </Box>

                <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', color: '#fff', textAlign: 'center', letterSpacing: '-0.01em', lineHeight: 1.25, mb: 1, px: 3, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                  Utilisez 2 doigts
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center', lineHeight: 1.45, px: 4, mb: 3 }}>
                  pour naviguer dans la carte de nos agences
                </Typography>

                <Box sx={{ width: 40, height: 1.5, bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 1, mb: 3 }} />

                {/* Bouton CTA — seul élément cliquable */}
                <Box
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dismissHint();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    pointerEvents: 'auto',
                    display:       'flex',
                    alignItems:    'center',
                    gap:           1,
                    bgcolor:       '#2e7d32',
                    border:        'none',
                    borderRadius:  9999,
                    px:            3,
                    py:            1.25,
                    cursor:        'pointer',
                    outline:       'none',
                    fontFamily:    'inherit',
                    boxShadow:     '0 4px 18px rgba(46,125,50,0.5)',
                    transition:    'transform 0.15s, box-shadow 0.15s',
                    '&:active':    { transform: 'scale(0.96)', boxShadow: '0 2px 8px rgba(46,125,50,0.4)' },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
                  </svg>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#fff', lineHeight: 1, whiteSpace: 'nowrap' }}>
                    Voir nos agences
                  </Typography>
                </Box>

                <Typography sx={{ position: 'absolute', bottom: 16, fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', letterSpacing: '0.04em' }}>
                         BICICI — Toutes nos agences en Côte d’Ivoire
                </Typography>
              </Box>
            )}

          </Box>{/* fin Carte */}

          {/* ── 2) BOTTOM SHEET ── */}
          {showList && (
            <Box
              sx={{
                flexShrink:          0,
                width:               '100%',
                height:              LISTE_H,
                display:             'flex',
                flexDirection:       'column',
                bgcolor:             '#ffffff',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                boxShadow:           '0 -6px 24px rgba(0,0,0,0.15)',
                overflow:            'hidden',
                zIndex:              Z.bottomSheet,
              }}
            >
              {/* EN-TÊTE */}
              <Box sx={{ flexShrink: 0, px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.15)' }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f5f5', borderRadius: 9999, px: 1.5, height: 44, border: '1.5px solid rgba(0,0,0,0.08)' }}>
                    <SearchIcon sx={{ color: '#888', fontSize: 20, flexShrink: 0 }} />
                    <Box
                      component="input"
                      placeholder="Rechercher une agence"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{
                        flex:       1,
                        border:     'none',
                        outline:    'none',
                        bgcolor:    'transparent',
                        fontSize:   '0.875rem',
                        color:      '#222',
                        fontFamily: 'inherit',
                        '&::placeholder': { color: '#aaa' },
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => { setShowList(false); setSearchQuery(''); setFilteredAgences(agences); }}
                    sx={{ bgcolor: 'rgba(0,0,0,0.07)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' }, width: 36, height: 36, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#2e7d32', lineHeight: 1 }}>
                  Résultats ({filteredAgences.length})
                </Typography>
              </Box>

              {/* LISTE SCROLLABLE */}
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, pt: 1.25, pb: 1, WebkitOverflowScrolling: 'touch' }}>
                {filteredAgences.map((agence) => (
                  <AgenceMobileCard
                    key={agence.id}
                    agence={agence}
                    selected={selectedAgence?.id === agence.id}
                    onClick={() => {
                      setSelectedAgence(agence);
                      if (map) { map.panTo(agence.position); map.setZoom(12); }
                    }}
                    onCall={handleCall}
                    onDirections={(a) => openDirections(a.position)}
                    onShare={handleShare}
                  />
                ))}
                {filteredAgences.length === 0 && !loading && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">Aucune agence trouvée</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── 3) BANDEAU VERT ── */}
          <Box
            sx={{
              flexShrink: 0,
              zIndex:     Z.bandeau,
              px:         { xs: 1.5, sm: 2 },
              pt:         { xs: 1.25, sm: 1.5 },
              pb:         `calc(12px + env(safe-area-inset-bottom, 0px))`,
              bgcolor:    'rgba(0, 59, 36, 1)',
              boxShadow:  '0 -4px 16px rgba(0,0,0,0.25)',
            }}
          >
            <Button
              fullWidth
              onClick={() => {
                if (showList) {
                  setShowList(false);
                  setSearchQuery('');
                  setFilteredAgences(agences);
                } else {
                  setFilteredAgences(agences);
                  setShowList(true);
                }
              }}
              variant="contained"
              sx={{
                background:    afficherListeBg,
                color:         '#062B23',
                fontWeight:    800,
                borderRadius:  2,
                height:        52,
                fontSize:      '0.85rem',
                letterSpacing: '0.04em',
                boxShadow:     'none',
                '&:hover':     { backgroundColor: 'rgba(127,240,142,1)', boxShadow: 'none' },
              }}
            >
              {showList ? 'FERMER LA LISTE' : 'AFFICHER LA LISTE'}
            </Button>
          </Box>

          {/* ── FICHE AGENCE SÉLECTIONNÉE (overlay flottant mobile/tablette) ──
              Sans image, design compact et professionnel.
              Layout : logo + nom/badge en-tête | infos | 2 boutons CTA
          */}
          {selectedAgence && (
            <Box
              sx={{
                position:     'absolute',
                bottom:       `${BANDEAU_H}px`,
                left:         12,
                right:        12,
                zIndex:       Z.agenceCard,
                bgcolor:      '#ffffff',
                borderRadius: '20px',
                boxShadow:    '0 -2px 0 0 rgba(46,125,50,0.18), 0 8px 32px rgba(0,0,0,0.18)',
                overflow:     'hidden',
              }}
            >
              {/* Bande verte supérieure fine */}
              <Box sx={{ height: 4, bgcolor: '#2e7d32', width: '100%' }} />

              <Box sx={{ p: 1.75 }}>

                {/* ── En-tête : logo + nom + badge + fermer ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                  {/* Logo */}
                  <Box sx={{
                    width: 40, height: 40, flexShrink: 0,
                    borderRadius: '12px', overflow: 'hidden',
                    bgcolor: '#0f5132',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Box
                      component="img"
                      src="/images/logobicici.png"
                      alt="BICICI"
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallback) return;
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = `${REACT_APP_LIFERAY_HOST}/documents/d/website-bicici/logobicici`;
                      }}
                      sx={{ width: '58%', height: '58%', objectFit: 'contain' }}
                    />
                  </Box>

                  {/* Nom + badge ouvert */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 800, fontSize: '0.92rem', color: '#111',
                      lineHeight: 1.25, mb: 0.3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {selectedAgence.nom}
                    </Typography>
                    {selectedAgence.ville ? (
                      <Typography sx={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 600 }}>
                        {selectedAgence.ville}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* Badge ouvert/fermé */}
                  {selectedAgence.ouvert ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>Ouvert</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#c62828' }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#c62828', lineHeight: 1 }}>Fermé</Typography>
                    </Box>
                  )}

                  {/* Bouton fermer */}
                  <IconButton
                    size="small"
                    onClick={() => setSelectedAgence(null)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' }, width: 28, height: 28, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>

                {/* ── Séparateur ── */}
                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', mb: 1.25 }} />

                {/* ── Infos agence (2 colonnes si adresse + tel) ── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mb: 1.5 }}>
                  {selectedAgence.adresse && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <MapPinIcon sx={{ fontSize: 14, color: '#2e7d32', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#444', lineHeight: 1.4 }}>
                        {selectedAgence.adresse}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.telephone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
                      <Typography
                        component="a"
                        href={`tel:${selectedAgence.telephone}`}
                        sx={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600, lineHeight: 1, textDecoration: 'none' }}
                      >
                        {selectedAgence.telephone}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.heures && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ClockIcon sx={{ fontSize: 14, color: '#888', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1 }}>
                        {selectedAgence.heures}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <MailIcon sx={{ fontSize: 14, color: '#888', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1 }}>
                        {selectedAgence.email}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Boutons CTA côte à côte ── */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    startIcon={<DirectionsIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openDirections(selectedAgence.position);
                    }}
                    sx={{
                      bgcolor:      '#2e7d32',
                      color:        '#fff',
                      fontWeight:   700,
                      fontSize:     '0.75rem',
                      borderRadius: '10px',
                      height:       40,
                      boxShadow:    'none',
                      textTransform: 'none',
                      '&:hover':    { bgcolor: '#1b5e20', boxShadow: 'none' },
                    }}
                  >
                    Itinéraire
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<ResetIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      resetMapView();
                    }}
                    sx={{
                      borderColor:  '#2e7d32',
                      color:        '#2e7d32',
                      fontWeight:   700,
                      fontSize:     '0.75rem',
                      borderRadius: '10px',
                      height:       40,
                      textTransform: 'none',
                      '&:hover':    { bgcolor: '#e8f5e9', borderColor: '#2e7d32' },
                    }}
                  >
                    Réinitialiser
                  </Button>
                </Box>

              </Box>
            </Box>
          )}
        </Box>

      ) : (

        /* ════════════════════════════════════════════════════════
           LAYOUT DESKTOP — 100% intact, aucun changement
           ════════════════════════════════════════════════════════ */
        <Box
          className="bicici-map-container"
          sx={{ width: '100%', height: FULL_H, position: 'relative', overflow: 'hidden', display: 'flex' }}
        >
          <Box ref={mapRef} sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', zIndex: 1 }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={(e) => { setCustomPin({ lat: e.latLng.lat(), lng: e.latLng.lng() }); setFullScreenSafe(false); }}
                options={{ gestureHandling: 'greedy', zoomControl: false, mapTypeControl: true, streetViewControl: false, fullscreenControl: false, rotateControl: false, scaleControl: false }}
              >
                {userPosition && (
                  <MarkerF position={userPosition} icon={{ url: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2196f3" width="24" height="24"><circle cx="12" cy="12" r="6"/></svg>`), scaledSize: new window.google.maps.Size(20, 20), anchor: new window.google.maps.Point(10, 10) }} />
                )}

                {/* ── HOVER desktop : logo + nom + téléphone ──
                    Style injecté pour masquer la croix et la bordure
                    native de Google Maps InfoWindow.               */}
                {hoveredAgence && !selectedAgence && (
                  <>
                    <style>{`
                      .gm-style .gm-style-iw-c {
                        padding: 0 !important;
                        border-radius: 14px !important;
                        box-shadow: 0 4px 20px rgba(0,0,0,0.18) !important;
                        border: none !important;
                        outline: none !important;
                      }
                      .gm-style .gm-style-iw-d {
                        overflow: hidden !important;
                        padding: 0 !important;
                      }
                      .gm-style .gm-style-iw-t::after {
                        background: #ffffff !important;
                        box-shadow: none !important;
                      }
                      .gm-style .gm-style-iw-tc::after {
                        background: #ffffff !important;
                      }
                      .gm-style-iw-chr {
                        display: none !important;
                      }
                      .gm-style .gm-style-iw {
                        border: none !important;
                        outline: none !important;
                      }
                      .gm-style-iw-c + div {
                        display: none !important;
                      }
                    `}</style>
                    <InfoWindowF
                      position={hoveredAgence.position}
                      onCloseClick={() => setHoveredAgence(null)}
                      options={{ disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -44) }}
                    >
                      <div
                        onMouseEnter={() => clearTimeout(hoverTimerRef.current)}
                        onMouseLeave={() => { hoverTimerRef.current = setTimeout(() => setHoveredAgence(null), 200); }}
                        style={{
                          display:    'flex',
                          alignItems: 'center',
                          gap:        '10px',
                          padding:    '10px 14px 10px 10px',
                          fontFamily: 'inherit',
                          outline:    'none',
                          minWidth:   0,
                          maxWidth:   240,
                        }}
                      >
                        {/* Logo */}
                        <div style={{
                          width: 34, height: 34, flexShrink: 0,
                          borderRadius: 9, overflow: 'hidden',
                          background: '#0f5132',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <img
                            src="/images/logobicici.png"
                            alt="BICICI"
                            onError={(e) => { e.currentTarget.src = `${REACT_APP_LIFERAY_HOST}/documents/d/website-bicici/logobicici`; }}
                            style={{ width: '60%', height: '60%', objectFit: 'contain', display: 'block' }}
                          />
                        </div>

                        {/* Séparateur vertical */}
                        <div style={{ width: 1, height: 32, background: 'rgba(0,0,0,0.08)', flexShrink: 0 }} />

                        {/* Nom + téléphone */}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            fontWeight: 700, fontSize: 13, color: '#111',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            lineHeight: 1.3, marginBottom: 4,
                          }}>
                            {hoveredAgence.nom}
                          </div>
                          {hoveredAgence.telephone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="#2e7d32"/>
                              </svg>
                              <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {hoveredAgence.telephone}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </InfoWindowF>
                  </>
                )}

                {customPin && (
                  <MarkerF position={customPin} draggable onDragEnd={(e) => setCustomPin({ lat: e.latLng.lat(), lng: e.latLng.lng() })} icon={{ url: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6d00" width="28" height="28"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>`), scaledSize: new window.google.maps.Size(28, 28), anchor: new window.google.maps.Point(14, 28) }} />
                )}
              </GoogleMap>
            ) : (
              <Box sx={{ width: '100%', height: '100%', background: 'transparent' }} />
            )}

            {showList && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: Z.desktopPanel, pointerEvents: 'none', display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ width: PANEL_WIDTH, height: '100%', bgcolor: 'background.paper', pointerEvents: 'auto', boxShadow: '0 12px 28px rgba(0,0,0,.25)', overflow: 'hidden', position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: Z.desktopPanel + 1, boxSizing: 'border-box' }}>
                  <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" color="primary">{filteredAgences.length > 0 ? `Résultats (${filteredAgences.length})` : 'Aucun résultat'}</Typography>
                      <IconButton onClick={() => setShowList(false)}><CloseIcon /></IconButton>
                    </Box>
                    <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#f9fafb', p: 1 }}>
                      {filteredAgences.map((agence) => (
                        <Box key={agence.id} onClick={() => { setSelectedAgence(agence); setShowList(false); if (map) { map.panTo(agence.position); map.setZoom(15); } }}
                          sx={{ mb: 2, cursor: 'pointer', borderRadius: 2, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', transition: 'box-shadow 0.25s ease', '&:hover': { boxShadow: '0 10px 24px rgba(0,0,0,0.18)' }, border: selectedAgence?.id === agence.id ? '2px solid' : 'none', borderColor: selectedAgence?.id === agence.id ? 'primary.main' : 'transparent', backgroundColor: 'background.paper', p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>{agence.nom}</Typography>
                          <Box component="img" src={agence.imageUrl || '/images/map.jpg'} alt={agence.nom} loading="lazy" onError={handleImgError} sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1.5, mb: 1.5 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><MapPinIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary">{agence.adresse}</Typography></Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary">{agence.telephone}</Typography></Box>
                          {agence.email && <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><MailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary">{agence.email}</Typography></Box>}
                          {agence.heures && <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><ClockIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /><Typography variant="body2" color="text.secondary">{agence.heures}</Typography></Box>}
                          <Button size="small" variant="outlined" startIcon={<DirectionsIcon />} onClick={(e) => { e.stopPropagation(); openDirections(agence.position); }}>Itinéraire</Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {customPin && (
              <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: Z.desktopPanel + 1, bgcolor: '#fff', p: 1, borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="caption">Lat: {customPin.lat.toFixed(6)} | Lng: {customPin.lng.toFixed(6)}</Typography>
              </Box>
            )}
            {loading && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: Z.desktopLoading, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress color="primary" size={48} />
                <Typography variant="body2" color="primary" fontWeight={600}>Chargement des agences...</Typography>
              </Box>
            )}

            <Box sx={{ position: 'absolute', bottom: `calc(16px + env(safe-area-inset-bottom))`, left: SEARCH_LEFT, transform: 'translateX(-50%)', width: SEARCH_WIDTH || { md: '72%' }, pointerEvents: 'auto', zIndex: Z.desktopSearch }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, bgcolor: 'rgba(0, 59, 36, 1)', borderRadius: 3, boxShadow: '0 14px 36px rgba(0,0,0,.35)' }}>
                <TextField inputRef={searchRef} fullWidth placeholder="Rechercher une agence" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} variant="outlined"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  sx={{ bgcolor: '#fff', borderRadius: 9999, '& .MuiOutlinedInput-root': { height: 56, borderRadius: 9999, '& fieldset': { border: 'none' }, '& input': { p: 0, px: 2 } } }}
                />
                <Typography variant="body2" sx={{ color: '#CDE8D5', fontWeight: 700, px: 1, textTransform: 'lowercase' }}>ou</Typography>
                <Button onClick={() => { setFullScreenSafe(false); setShowList((s) => !s); }} variant="contained"
                  sx={{ background: afficherListeBg, color: '#062B23', fontWeight: 800, borderRadius: 2, width: '100%', height: 56, '&:hover': { backgroundColor: 'rgba(127, 240, 142, 1)' } }}>
                  {showList ? 'FERMER LA LISTE' : 'AFFICHER LA LISTE'}
                </Button>
              </Box>
            </Box>

            <IconButton onClick={handleLocateUser} sx={{ position: 'absolute', top: 16, left: 16, bgcolor: '#fff', border: '2px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' }, zIndex: Z.desktopButtons }}>
              <NavigationIcon color="primary" />
            </IconButton>
            <IconButton onClick={resetMapView} sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#fff', border: '2px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' }, zIndex: Z.desktopButtons }} title="Retour à la carte">
              <ResetIcon color="primary" />
            </IconButton>
          </Box>

          {/* ── FICHE AGENCE SÉLECTIONNÉE desktop — même design que mobile,
                positionnée en bas à gauche pour ne pas gêner la carte ── */}
          {selectedAgence && (
            <Box
              sx={{
                position:     'absolute',
                bottom:       88,   /* au-dessus de la barre de recherche */
                left:         20,
                width:        340,
                zIndex:       Z.desktopPanel + 2,
                bgcolor:      '#ffffff',
                borderRadius: '20px',
                boxShadow:    '0 -2px 0 0 rgba(46,125,50,0.18), 0 8px 32px rgba(0,0,0,0.22)',
                overflow:     'hidden',
                /* Petite animation d'apparition */
                animation:    'slideUp 0.22s cubic-bezier(0.4,0,0.2,1)',
                '@keyframes slideUp': {
                  from: { opacity: 0, transform: 'translateY(12px)' },
                  to:   { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {/* Bande verte supérieure fine */}
              <Box sx={{ height: 4, bgcolor: '#2e7d32', width: '100%' }} />

              <Box sx={{ p: 1.75 }}>

                {/* ── En-tête : logo + nom + ville + badge + fermer ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{
                    width: 40, height: 40, flexShrink: 0,
                    borderRadius: '12px', overflow: 'hidden',
                    bgcolor: '#0f5132',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Box
                      component="img"
                      src="/images/logobicici.png"
                      alt="BICICI"
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallback) return;
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = `${REACT_APP_LIFERAY_HOST}/documents/d/website-bicici/logobicici`;
                      }}
                      sx={{ width: '58%', height: '58%', objectFit: 'contain' }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 800, fontSize: '0.92rem', color: '#111',
                      lineHeight: 1.25, mb: 0.3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {selectedAgence.nom}
                    </Typography>
                    {selectedAgence.ville ? (
                      <Typography sx={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 600 }}>
                        {selectedAgence.ville}
                      </Typography>
                    ) : null}
                  </Box>

                  {selectedAgence.ouvert ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>Ouvert</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#fce4ec', border: '1px solid #f8bbd0', borderRadius: 9999, px: '8px', py: '3px', flexShrink: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#c62828' }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#c62828', lineHeight: 1 }}>Fermé</Typography>
                    </Box>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => setSelectedAgence(null)}
                    sx={{ bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' }, width: 28, height: 28, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>

                {/* ── Séparateur ── */}
                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', mb: 1.25 }} />

                {/* ── Infos ── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mb: 1.5 }}>
                  {selectedAgence.adresse && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <MapPinIcon sx={{ fontSize: 14, color: '#2e7d32', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#444', lineHeight: 1.4 }}>
                        {selectedAgence.adresse}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.telephone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
                      <Typography
                        component="a"
                        href={`tel:${selectedAgence.telephone}`}
                        sx={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600, lineHeight: 1, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {selectedAgence.telephone}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.heures && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ClockIcon sx={{ fontSize: 14, color: '#888', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1 }}>
                        {selectedAgence.heures}
                      </Typography>
                    </Box>
                  )}
                  {selectedAgence.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <MailIcon sx={{ fontSize: 14, color: '#888', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1 }}>
                        {selectedAgence.email}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Boutons CTA côte à côte ── */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    startIcon={<DirectionsIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => openDirections(selectedAgence.position)}
                    sx={{
                      bgcolor:       '#2e7d32',
                      color:         '#fff',
                      fontWeight:    700,
                      fontSize:      '0.75rem',
                      borderRadius:  '10px',
                      height:        40,
                      boxShadow:     'none',
                      textTransform: 'none',
                      '&:hover':     { bgcolor: '#1b5e20', boxShadow: 'none' },
                    }}
                  >
                    Itinéraire
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<ResetIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={resetMapView}
                    sx={{
                      borderColor:   '#2e7d32',
                      color:         '#2e7d32',
                      fontWeight:    700,
                      fontSize:      '0.75rem',
                      borderRadius:  '10px',
                      height:        40,
                      textTransform: 'none',
                      '&:hover':     { bgcolor: '#e8f5e9', borderColor: '#2e7d32' },
                    }}
                  >
                    Réinitialiser
                  </Button>
                </Box>

              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Snackbar "aucun résultat" */}
      <Snackbar open={Boolean(notFoundMsg)} autoHideDuration={null} onClose={() => setNotFoundMsg('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setNotFoundMsg('')} severity="warning" variant="filled" sx={{ width: '100%', bgcolor: 'hsla(162, 50%, 28%, 1)', color: '#FFFFFF', fontWeight: 600 }}>
          {notFoundMsg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
    </div>
  );
};

export default BiciciMap;
